import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { makeStyles } from '@mui/styles';
import { Box, Tooltip } from '@mui/material';
import { keyframes } from '@mui/system';
import { selectSessionId } from '../../redux/features/sessionBoundary/sessionBoundary.selectors';
import {
  selectFeatureFlagHitl3,
  selectFeatureFlagHitlExpressTimer,
  selectHitlExpressTimerMaxSeconds,
  selectIsHitl7Enabled,
} from '../../redux/features/config/config.selector';
import {
  selectIsExpressTimerElapsed,
  selectTaskAssignedSessionId,
  selectTaskAssignedSessionStartedAt,
} from '../../redux/features/taskRouter/taskRouter.selector';
import { selectIsCarExited } from '../../redux/features/sessionBoundary/sessionBoundary.selectors';
import { setExpressTimerElapsed } from '../../redux/features/taskRouter/taskRouter.slice';
import Colors from '../../utils/color';

const useStyles = makeStyles(() => ({
  container: {
    minWidth: '48px',
    margin: '0px !important',
    textAlign: 'center',
    flexShrink: 0,
  },
}));

// Gentle 1s background pulse for the over-threshold alert state. Declared once
// at module scope (emotion `keyframes`) so it is not re-created per render and
// no `makeStyles` rule is added (keeps the flag-off render and snapshots stable).
const expressTimerPulse = keyframes({
  '0%, 100%': { backgroundColor: Colors.guardsmanRed },
  '50%': { backgroundColor: Colors.bittersweet },
});

// HITL-1662 — the 7.0 over-threshold swell. Same two reds as the take-over
// alert (AIStatusBand), at 4s instead of 0.7s: identical colours say "a human
// is needed", and the tempo alone carries which one is urgent.
//
// It also retires `bittersweet` from the header. White on #FC7373 is 2.7:1,
// the least legible pairing in the console; requestStaff is 3.9:1 and #6b0000
// is 12.9:1, so the chip stays readable across the whole cycle.
const expressTimerPulseHitl7 = keyframes({
  '0%, 100%': { backgroundColor: Colors.requestStaff },
  '50%': { backgroundColor: '#6b0000' },
});

// This component will show the duration of the active session
export const Timer = () => {
  const classes = useStyles();
  const sessionId = useAppSelector(selectSessionId);
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  const isHitl7 = useAppSelector(selectIsHitl7Enabled);
  const isExpressTimerEnabled = useAppSelector(
    selectFeatureFlagHitlExpressTimer
  );
  const maxSeconds = useAppSelector(selectHitlExpressTimerMaxSeconds);
  const sessionStartedAt = useAppSelector(selectTaskAssignedSessionStartedAt);
  // The session that anchor was issued FOR. taskRouter stores the two together
  // on task_assigned, but only refreshes them on the NEXT assignment — so when
  // one assignment spans several cars, the anchor outlives its own session.
  const anchorSessionId = useAppSelector(selectTaskAssignedSessionId);
  const alreadyElapsed = useAppSelector(selectIsExpressTimerElapsed);
  const carExited = useAppSelector(selectIsCarExited);
  const dispatch = useAppDispatch();
  const [duration, setDuration] = useState(0);

  // The express timer only runs in the new UX (hitl_3_0) with the express-timer
  // flag on. When active and the backend supplied a session start time, the
  // elapsed time is anchored to it; otherwise we keep the original behavior
  // (count from when the timer mounts).
  const expressActive = isHitl3 && isExpressTimerEnabled;

  // HITL-1663: only anchor to the task_assigned start time while it still
  // describes the session on screen. A second car arriving under the same
  // assignment inherits the first car's start, and then the countdown reads the
  // previous order's elapsed time and immediately re-latches express mode on a
  // customer who has been at the board for two seconds. An anchor from another
  // session is no anchor: fall back to counting from mount, which is what this
  // component already does whenever the backend supplies no start time.
  //
  // 7.0 only. The same staleness exists in useExpressTimerLatch, which owns
  // this job with the flag off — see the note there.
  const anchorMatchesSession = !isHitl7 || anchorSessionId === sessionId;

  const getFormatedDuration = () => {
    const minutes = Math.floor(duration / 60);
    const seconds = duration - minutes * 60;
    const secondsToString =
      seconds > 9 ? String(seconds) : '0' + String(seconds);
    return String(minutes) + ':' + secondsToString;
  };

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (sessionId) {
      // Anchor to the session start time from the task_assigned event when the
      // express timer is active and a valid timestamp is present; otherwise fall
      // back to the original mount-time anchor.
      const parsedStart =
        expressActive && anchorMatchesSession
          ? new Date(sessionStartedAt).getTime()
          : NaN;
      const startTime =
        !isNaN(parsedStart) && sessionStartedAt
          ? parsedStart
          : new Date().getTime();

      const getDuration = () => {
        const durationInSecond = Math.max(
          0,
          Math.floor((new Date().getTime() - startTime) / 1000)
        );
        setDuration(durationInSecond);
      };
      // Compute immediately so a past anchor shows the right value on first paint.
      getDuration();
      timer = setInterval(() => {
        getDuration();
      }, 1000);
    }

    return () => {
      if (timer) {
        clearInterval(timer);
        setDuration(0);
      }
    };
  }, [sessionId, expressActive, sessionStartedAt, anchorMatchesSession]);

  const formatted = getFormatedDuration();

  // Escalating alert: calm -> amber as the order nears the limit -> red + pulse
  // once it exceeds the limit. Computed before the early return so the latch
  // effect below keeps a stable hook order.
  const over = expressActive && maxSeconds > 0 && duration > maxSeconds;
  const nearing =
    expressActive &&
    maxSeconds > 0 &&
    !over &&
    duration >= Math.floor(maxSeconds * 0.8);

  // The Timer is the source of truth for elapsed time (it counts from the
  // task_assigned anchor when present, else from mount). When it crosses the
  // threshold, latch isExpressTimerElapsed so express mode — both the upsell
  // suppression and the header bolt indicator — engages in lockstep with the
  // red timer, even in sessions that never received a task_assigned
  // session_started_at (where useExpressTimer cannot anchor and so never
  // latches on its own).
  useEffect(() => {
    // Do not re-latch once the car has exited: session_started_at is not cleared
    // until the next assignment, so the still-past-limit duration would fight the
    // car-exit reset in useExpressTimer in an infinite render loop.
    if (over && !alreadyElapsed && !carExited) {
      dispatch(setExpressTimerElapsed(true));
    }
  }, [over, alreadyElapsed, carExited, dispatch]);

  // Flag-off / no-threshold path: render exactly as before (no behavior or
  // snapshot change when the express timer is not active).
  if (!expressActive || maxSeconds <= 0) {
    return (
      <div
        className={classes.container}
        // 7.0 sizes the countdown like the header's other standing text — the
        // build version and the local clock, both 0.75rem — so it reads as part
        // of that row instead of shouting one size larger than everything else.
        style={isHitl7 ? { fontSize: '0.75rem' } : undefined}
      >
        {formatted}
      </div>
    );
  }

  const chip = (
    <Box
      className={classes.container}
      data-testid="express-timer"
      sx={{
        borderRadius: '4px',
        // 7.0 drops the horizontal padding entirely — the container's 48px
        // minWidth already gives the fill room around a 4-character time, so
        // the padding was only widening the chip past what it needed.
        padding: isHitl7 ? 0 : '0 6px',
        fontWeight: nearing || over ? 700 : 400,
        ...(isHitl7 && { fontSize: '0.75rem' }),
        // 7.0 fills the chip in EVERY state, not just over-threshold. With only
        // the red state filled, the countdown changed shape as well as colour
        // when it escalated, so the calm state read as "no chip" rather than as
        // "fine" — and the ladder had no green at all despite being described
        // as one. Now it is one pill that changes colour, and those colours are
        // the AI status band's: same meaning, same colour, wherever you look.
        //
        //   state     fill                       text
        //   ────────  ─────────────────────────  ──────────
        //   calm      brightGreen (header icons) near-black
        //   nearing   acknowledge (intents)      near-black
        //   over      requestStaff (Staff btn)   white
        ...(isHitl7 &&
          !nearing &&
          !over && {
            backgroundColor: Colors.brightGreen,
            color: Colors.primaryButtonTextColor,
          }),
        // Flag off, nearing stays amber TEXT on no fill, exactly as before.
        // `amber` and the band's `acknowledge` are already the same hex
        // (#ffc107) under two token names, so no colour change is needed here
        // to match — only the fill is new.
        ...(nearing &&
          (isHitl7
            ? {
                backgroundColor: Colors.acknowledge,
                color: Colors.primaryButtonTextColor,
              }
            : { color: Colors.amber })),
        ...(over && {
          color: Colors.white,
          backgroundColor: isHitl7 ? Colors.requestStaff : Colors.guardsmanRed,
          // 7.0 slows this to 4s AND moves it onto the take-over alert's reds.
          // At 1s in its own colours it beat in step with that alert, and two
          // things pulsing at the same rate in different reds read as one
          // texture rather than two signals. Now: same colours, different
          // tempo — a hard 0.7s flash means act, a slow 4s swell means watch.
          animation: `${isHitl7 ? expressTimerPulseHitl7 : expressTimerPulse} ${
            isHitl7 ? '4s' : '1s'
          } ease-in-out infinite`,
        }),
      }}
    >
      {formatted}
    </Box>
  );

  if (over || nearing) {
    return (
      <Tooltip
        title={
          over
            ? `Over ${maxSeconds}s target`
            : `Approaching ${maxSeconds}s target`
        }
        arrow
      >
        {chip}
      </Tooltip>
    );
  }

  return chip;
};
